import React, { useState, useCallback, useEffect, useRef } from "react";
import DailyProductionWidget from "./DailyProductionWidget";
import {
  fetchManagersPage,
  fetchRecruitersPage,
  DEFAULT_COLUMN_HEADERS,
} from "./DummyData";
import TableAdv from "../Table/TableAdv/TableAdv";

const MODAL_COLUMNS = {
  candidates: [
    { key: "name", label: "Name", render: (r) => r.name },
    { key: "email", label: "Email", render: (r) => r.email ?? "—" },
    { key: "city", label: "City", render: (r) => r.city ?? "—" },
    { key: "jobTitle", label: "Job Title", render: (r) => r.jobTitle ?? "—" },
  ],
  submissions: [
    {
      key: "candidateName",
      label: "Candidate",
      render: (r) => r.candidateName ?? r.name,
    },
    { key: "jobTitle", label: "Job Title", render: (r) => r.jobTitle ?? "—" },
    { key: "company", label: "Company", render: (r) => r.company ?? "—" },
    { key: "date", label: "Date", render: (r) => r.date ?? "—" },
  ],
  interviews: [
    {
      key: "candidateName",
      label: "Candidate",
      render: (r) => r.candidateName ?? r.name,
    },
    { key: "jobTitle", label: "Job Title", render: (r) => r.jobTitle ?? "—" },
    { key: "company", label: "Company", render: (r) => r.company ?? "—" },
    { key: "status", label: "Status", render: () => "Ready for interview" },
  ],
  placements: [
    {
      key: "candidateName",
      label: "Candidate",
      render: (r) => r.candidateName ?? r.name,
    },
    { key: "jobTitle", label: "Job Title", render: (r) => r.jobTitle ?? "—" },
    { key: "company", label: "Company", render: (r) => r.company ?? "—" },
    { key: "location", label: "Location", render: (r) => r.location ?? "—" },
    {
      key: "status",
      label: "Status",
      render: () => <span className="text-green-600 font-medium">Placed</span>,
    },
  ],
};

/** Build placeholder detail rows from the count on the employee object */
const buildDetailRows = (employee, metricKey) => {
  const count = employee?.[metricKey] ?? 0;
  return Array.from({ length: count }, (_, i) => ({
    id: `${employee?.id}-${metricKey}-${i}`,
    name: `${employee?.name} – Record ${i + 1}`,
    candidateName: `Candidate ${i + 1}`,
    email: `candidate${i + 1}@example.com`,
    city: ["New York", "San Francisco", "Austin", "Chicago", "Seattle"][i % 5],
    jobTitle: [
      "React Developer",
      "Java Engineer",
      "DevOps Lead",
      "QA Engineer",
      "Data Scientist",
    ][i % 5],
    company: ["Acme Corp", "TechNova", "CloudSoft", "DataPeak", "InfraHub"][
      i % 5
    ],
    date: new Date(Date.now() - i * 86400000 * 3).toLocaleDateString(),
    location: ["Remote", "Hybrid", "On-site"][i % 3],
  }));
};


const DailyProductionWidgetPage = () => {
  // ── Manager state ──
  const [managers, setManagers] = useState([]);
  const [managerPage, setManagerPage] = useState(1);
  const [hasMoreManagers, setHasMoreManagers] = useState(true);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // ── Recruiter (SubItems) state (Multi-Expand Architecture) ──
  const [expandedManagerIds, setExpandedManagerIds] = useState([]);
  const [recruitersByManager, setRecruitersByManager] = useState({});
  const [recruiterPages, setRecruiterPages] = useState({});
  const [hasMoreRecruitersMap, setHasMoreRecruitersMap] = useState({});
  const [loadingRecruitersMap, setLoadingRecruitersMap] = useState({});

  // ── Modal state ──
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalRows, setModalRows] = useState([]);
  const [modalColumns, setModalColumns] = useState([]);

  // ── Initial manager load ──
  useEffect(() => {
    loadManagers(1);
  }, []);

  const loadManagers = useCallback(
    async (page) => {
      if (loadingManagers) return;
      setLoadingManagers(true);
      try {
        const result = await fetchManagersPage(page);
        setManagers((prev) =>
          page === 1 ? result.data : [...prev, ...result.data],
        );
        setHasMoreManagers(result.hasMore);
        setManagerPage(page);
      } finally {
        setLoadingManagers(false);
      }
    },
    [loadingManagers],
  );

  // ── Load more managers (triggered by scroll sentinel) ──
  const handleLoadMoreManagers = useCallback(() => {
    if (!loadingManagers && hasMoreManagers) {
      loadManagers(managerPage + 1);
    }
  }, [loadManagers, managerPage, loadingManagers, hasMoreManagers]);

  // ── Manager selection toggle: load first page of recruiters ──
  const handleManagerToggle = useCallback(
    async (manager) => {
      const isExpanded = expandedManagerIds.includes(manager.id);
      
      if (isExpanded) {
        // Collapse: Remove ID from expanded list
        setExpandedManagerIds(prev => prev.filter(id => id !== manager.id));
      } else {
        // Expand: Add ID to expanded list
        setExpandedManagerIds(prev => [...prev, manager.id]);
        
        // Fetch recruiters if we haven't already fetched them for this manager
        if (!recruitersByManager[manager.id]) {
          setLoadingRecruitersMap(prev => ({ ...prev, [manager.id]: true }));
          try {
            const result = await fetchRecruitersPage(manager.id, 1);
            setRecruitersByManager(prev => ({ ...prev, [manager.id]: result.data }));
            setHasMoreRecruitersMap(prev => ({ ...prev, [manager.id]: result.hasMore }));
            setRecruiterPages(prev => ({ ...prev, [manager.id]: 1 }));
          } finally {
            setLoadingRecruitersMap(prev => ({ ...prev, [manager.id]: false }));
          }
        }
      }
    },
    [expandedManagerIds, recruitersByManager]
  );

  // ── Load more recruiters ──
  const handleLoadMoreRecruiters = useCallback(
    async (managerId) => {
      const isLoading = loadingRecruitersMap[managerId];
      const hasMore = hasMoreRecruitersMap[managerId];
      const currentPage = recruiterPages[managerId] || 1;

      if (!managerId || isLoading || !hasMore) return;
      
      setLoadingRecruitersMap(prev => ({ ...prev, [managerId]: true }));
      try {
        const nextPage = currentPage + 1;
        const result = await fetchRecruitersPage(managerId, nextPage);
        
        setRecruitersByManager(prev => ({
          ...prev,
          [managerId]: [...(prev[managerId] || []), ...result.data]
        }));
        
        setHasMoreRecruitersMap(prev => ({
          ...prev,
          [managerId]: result.hasMore
        }));
        
        setRecruiterPages(prev => ({
          ...prev,
          [managerId]: nextPage
        }));
      } finally {
        setLoadingRecruitersMap(prev => ({ ...prev, [managerId]: false }));
      }
    },
    [loadingRecruitersMap, hasMoreRecruitersMap, recruiterPages]
  );

  // ── Metric click: build modal content ──
  const handleMetricClick = useCallback((entity, header) => {
    const rows = buildDetailRows(entity, header.key);
    const cols = MODAL_COLUMNS[header.key] ?? [];
    setModalRows(rows);
    setModalColumns(cols);
    setModalTitle(`${entity?.name ?? ""} — ${header.label}`);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => setIsModalOpen(false), []);

  // ── Modal content node ──
  const modalContent = (
    <TableAdv rows={modalRows} columns={modalColumns} />
  );
console.log(managers)
  return (
    <div className="w-full h-[calc(100vh-130px)] flex flex-col overflow-hidden bg-gray-100">

      {/* Widget fills remaining height */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <DailyProductionWidget
          // Data
          items={managers}
          subItems={recruitersByManager}
          columnHeaders={DEFAULT_COLUMN_HEADERS}
          // Pagination
          hasMoreItems={hasMoreManagers}
          hasMoreSubItems={hasMoreRecruitersMap}
          // Loading
          loadingStates={{
            items: loadingManagers,
            subItems: loadingRecruitersMap,
          }}
          // Selection (multiple expanded)
          expandedItemIds={expandedManagerIds}
          // Callbacks
          onItemToggle={handleManagerToggle}
          onLoadMoreItems={handleLoadMoreManagers}
          onLoadMoreSubItems={handleLoadMoreRecruiters}
          onMetricClick={handleMetricClick}
          // Modal (controlled)
          isModalOpen={isModalOpen}
          onModalClose={handleModalClose}
          modalTitle={modalTitle}
          modalContent={modalContent}
        />
      </div>
    </div>
  );
};

export default DailyProductionWidgetPage;
